import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from "../../../components/ui/Button";
import { DEFAULT_QUOTATION_COLUMNS, DEFAULT_QUOTATION_ROW } from '../constants/quotation_constants';

export const InitialQuotationTable = ({ onTotalChange, onDataChange, initialRows, initialDiscount, initialGst, stockSelections = {} }) => {
  const [columns, setColumns] = useState({ ...DEFAULT_QUOTATION_COLUMNS });
  const [rows, setRows] = useState(initialRows && initialRows.length > 0 ? initialRows : [
    { id: 1, ...DEFAULT_QUOTATION_ROW, amount: 0, isStock: false, isLocked: false, _stockKey: null }
  ]);

  useEffect(() => {
    if (initialRows && initialRows.length > 0) {
      if (JSON.stringify(initialRows) !== JSON.stringify(rows)) {
        setRows(initialRows);
      }
    }
  }, [initialRows]);

  const [discountType, setDiscountType] = useState(initialDiscount?.type || 'percentage');
  const [discountValue, setDiscountValue] = useState(initialDiscount?.value !== undefined ? initialDiscount.value : 10);

  const [gstType, setGstType] = useState(initialGst?.type || 'percentage');
  const [gstValue, setGstValue] = useState(initialGst?.value !== undefined ? initialGst.value : 22);

  const onTotalChangeRef = useRef(onTotalChange);
  const onDataChangeRef = useRef(onDataChange);

  useEffect(() => {
    onTotalChangeRef.current = onTotalChange;
    onDataChangeRef.current = onDataChange;
  });

  const prevStockRef = useRef({});
  useEffect(() => {
    const currentKeys = Object.keys(stockSelections);
    const prevKeys = Object.keys(prevStockRef.current);

    const added = currentKeys.filter(k => !prevKeys.includes(k));
    const removed = prevKeys.filter(k => !currentKeys.includes(k));

    if (added.length > 0 || removed.length > 0) {
      setRows(currentRows => {
        let newRows = [...currentRows];
        // Remove deselected
        removed.forEach(key => {
          newRows = newRows.filter(r => r._stockKey !== key);
        });
        // Add newly selected
        added.forEach(key => {
          const item = stockSelections[key];
          newRows.push({
            id: Date.now() + Math.random(),
            type: 'item',
            particulars: item.flatItem.itemName,
            colourType: item.flatItem.vendor,
            warranty: item.flatItem.invoiceNo,
            quantity: item.qty,
            amount: item.flatItem.purchaseRate || 0,
            _stockKey: key,
            isStock: true,
            isLocked: true,
            _fromStock: true
          });
        });
        return newRows;
      });
    }

    // Handle quantity updates for existing stock selections
    currentKeys.forEach(key => {
       if (prevKeys.includes(key)) {
          if (stockSelections[key].qty !== prevStockRef.current[key].qty) {
             setRows(currentRows => currentRows.map(r => r._stockKey === key ? { ...r, quantity: stockSelections[key].qty } : r));
          }
       }
    });

    prevStockRef.current = stockSelections;
  }, [stockSelections]);

  useEffect(() => {
    const subTotal = rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
    const discountAmount = discountType === 'percentage'
      ? (subTotal * (Number(discountValue) || 0) / 100)
      : (Number(discountValue) || 0);
    const taxableAmount = subTotal - discountAmount;

    const gstAmount = gstType === 'percentage'
      ? (taxableAmount * (Number(gstValue) || 0) / 100)
      : (Number(gstValue) || 0);

    const grandTotal = taxableAmount + gstAmount;

    if (onTotalChangeRef.current) {
      onTotalChangeRef.current(grandTotal);
    }
    if (onDataChangeRef.current) {
      onDataChangeRef.current({
        rows,
        subTotal,
        discountAmount,
        gstAmount,
        grandTotal,
        discountType,
        discountValue,
        gstType,
        gstValue
      });
    }
  }, [rows, discountType, discountValue, gstType, gstValue]);

  const addRow = () => {
    setRows([
      ...rows,
      { id: Date.now(), type: 'item', ...DEFAULT_QUOTATION_ROW, amount: 0, isStock: false, isLocked: false, _stockKey: null, _fromStock: false }
    ]);
  };

  const addHorizontalRow = () => {
    setRows([
      ...rows,
      { id: Date.now(), type: 'horizontal', particulars: '' }
    ]);
  };

  const removeRow = (id) => {
    setRows(rows.filter(row => row.id !== id));
  };

  const handleRowChange = (id, field, value) => {
    setRows(rows.map(row => {
      if (row.id !== id) return row;

      // Fields from stock should not be editable
      if (row._fromStock && ['particulars', 'quantity', 'amount', 'colourType', 'warranty'].includes(field)) {
        return row;
      }

      const updatedRow = { ...row, [field]: value };

      if (field === 'quantity') {
        const newQty = Number(value) || 0;
        const currentQty = Number(row.quantity) || 1;
        const currentAmt = Number(row.amount) || 0;
        const unitRate = row.unitRate !== undefined ? Number(row.unitRate) : (currentQty > 0 ? currentAmt / currentQty : currentAmt);
        updatedRow.unitRate = unitRate;
        updatedRow.amount = Math.round((newQty * unitRate) * 100) / 100;
      } else if (field === 'amount') {
        const newAmt = Number(value) || 0;
        const currentQty = Number(row.quantity) || 1;
        updatedRow.unitRate = currentQty > 0 ? newAmt / currentQty : newAmt;
      }

      return updatedRow;
    }));
  };

  const handleColumnChange = (field, value) => {
    setColumns(prev => ({ ...prev, [field]: value }));
  };

  const subTotal = rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  const discountAmount = discountType === 'percentage'
    ? (subTotal * (Number(discountValue) || 0) / 100)
    : (Number(discountValue) || 0);
  const taxableAmount = subTotal - discountAmount;

  const gstAmount = gstType === 'percentage'
    ? (taxableAmount * (Number(gstValue) || 0) / 100)
    : (Number(gstValue) || 0);

  const grandTotal = taxableAmount + gstAmount;

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex justify-end items-center mb-1">
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Plus size={14} />}
            onClick={addHorizontalRow}
            className="text-slate-600 border-slate-300 hover:bg-slate-50 h-8 text-xs"
          >
            Row
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Plus size={14} />}
            onClick={addRow}
            className="text-brand-blue border-brand-blue hover:bg-blue-50 h-8 text-xs"
          >
            Item
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="w-full text-sm text-left text-slate-600 min-w-[850px]">
          <thead className="text-[11px] text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
            <tr>
              <th scope="col" className="px-2 py-2 w-[35%]">
                <input
                  type="text"
                  value={columns.col1}
                  onChange={(e) => handleColumnChange('col1', e.target.value)}
                  className="w-full bg-transparent border-none outline-none font-medium text-slate-500 uppercase p-0 focus:ring-0"
                  placeholder="Particulars"
                />
              </th>
              <th scope="col" className="px-2 py-2 w-[14%]">
                <input
                  type="text"
                  value={columns.col2}
                  onChange={(e) => handleColumnChange('col2', e.target.value)}
                  className="w-full bg-transparent border-none outline-none font-medium text-slate-500 uppercase p-0 focus:ring-0"
                  placeholder="Colour / Type"
                />
              </th>
              <th scope="col" className="px-2 py-2 w-[18%]">
                <input
                  type="text"
                  value={columns.col3}
                  onChange={(e) => handleColumnChange('col3', e.target.value)}
                  className="w-full bg-transparent border-none outline-none font-medium text-slate-500 uppercase p-0 focus:ring-0"
                />
              </th>
              <th scope="col" className="px-2 py-2 w-[10%]">
                <input
                  type="text"
                  value={columns.colQty || "QUANTITY"}
                  onChange={(e) => handleColumnChange('colQty', e.target.value)}
                  className="w-full bg-transparent border-none outline-none font-medium text-slate-500 uppercase p-0 focus:ring-0"
                  placeholder="Quantity"
                />
              </th>
              <th scope="col" className="px-2 py-2 w-[13%] text-right">
                <input
                  type="text"
                  value={columns.col4}
                  onChange={(e) => handleColumnChange('col4', e.target.value)}
                  className="w-full bg-transparent border-none outline-none font-medium text-slate-500 uppercase p-0 focus:ring-0 text-right"
                />
              </th>
              <th scope="col" className="px-2 py-2 w-[8%] text-center text-[10px] font-semibold text-slate-500">
                Stock Item
              </th>
              <th scope="col" className="px-2 py-2 w-[5%] text-center"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className={`bg-white border-b border-slate-100 hover:bg-slate-50 transition-colors ${row.type === 'horizontal' ? 'bg-slate-50 font-medium' : ''}`}>
                {row.type === 'horizontal' ? (
                  <>
                    <td className="px-2 py-1" colSpan={6}>
                      <input
                        type="text"
                        value={row.particulars}
                        onChange={(e) => handleRowChange(row.id, 'particulars', e.target.value)}
                        className="w-full px-1 py-1 text-sm bg-transparent border border-transparent hover:border-slate-300 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue rounded outline-none transition-all font-semibold text-slate-800 text-center"
                        placeholder="Enter section heading or note..."
                      />
                    </td>
                    <td className="px-2 py-1 text-center">
                      <button
                        onClick={() => removeRow(row.id)}
                        className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="Remove row"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-2 py-2 align-top">
                      <div className="relative">
                        <textarea
                          rows={3}
                          value={row.particulars}
                          readOnly={Boolean(row._fromStock)}
                          onChange={(e) => handleRowChange(row.id, 'particulars', e.target.value)}
                          className={`w-full px-2 py-1.5 text-[13px] rounded outline-none transition-all resize-y min-h-[70px] leading-relaxed ${
                            row._fromStock
                              ? 'bg-slate-100 text-slate-600 cursor-not-allowed border-transparent select-none'
                              : 'bg-transparent border border-transparent hover:border-slate-300 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue'
                          }`}
                          placeholder="Enter particulars... (Use Shift+Enter for multiple lines)"
                        />
                        {row._fromStock && (
                          <span className="absolute top-1.5 right-2 text-[9px] font-bold uppercase tracking-wider bg-blue-100 text-brand-blue px-1.5 py-0.5 rounded">
                            Stock
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-2 align-top pt-3">
                      <input
                        type="text"
                        value={row.colourType}
                        readOnly={Boolean(row._fromStock)}
                        onChange={(e) => handleRowChange(row.id, 'colourType', e.target.value)}
                        className={`w-full px-1 py-1 text-[13px] rounded outline-none transition-all ${
                          row._fromStock
                            ? 'bg-slate-100 text-slate-600 cursor-not-allowed border-transparent select-none'
                            : 'bg-transparent border border-transparent hover:border-slate-300 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue'
                        }`}
                        placeholder="Marine club"
                      />
                    </td>
                    <td className="px-2 py-2 align-top pt-3">
                      <input
                        type="text"
                        value={row.warranty}
                        readOnly={Boolean(row._fromStock)}
                        onChange={(e) => handleRowChange(row.id, 'warranty', e.target.value)}
                        className={`w-full px-1 py-1 text-[13px] rounded outline-none transition-all ${
                          row._fromStock
                            ? 'bg-slate-100 text-slate-600 cursor-not-allowed border-transparent select-none'
                            : 'bg-transparent border border-transparent hover:border-slate-300 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue'
                        }`}
                        placeholder="7 years"
                      />
                    </td>
                    <td className="px-2 py-2 align-top pt-3">
                      <input
                        type="number"
                        min="0"
                        value={row.quantity || ''}
                        readOnly={Boolean(row._fromStock)}
                        onChange={(e) => handleRowChange(row.id, 'quantity', e.target.value)}
                        className={`w-full px-1 py-1 text-[13px] rounded outline-none transition-all text-center font-medium text-slate-700 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                          row._fromStock
                            ? 'bg-slate-100 text-slate-600 cursor-not-allowed border-transparent select-none'
                            : 'bg-transparent border border-transparent hover:border-slate-300 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue'
                        }`}
                        placeholder="Qty"
                      />
                    </td>
                    <td className="px-2 py-2 align-top pt-3">
                      <input
                        type="number"
                        min="0"
                        value={row.amount}
                        readOnly={Boolean(row._fromStock)}
                        onChange={(e) => handleRowChange(row.id, 'amount', e.target.value)}
                        className={`w-full px-1 py-1 text-[13px] rounded outline-none transition-all text-right font-medium text-slate-700 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                          row._fromStock
                            ? 'bg-slate-100 text-slate-600 cursor-not-allowed border-transparent select-none'
                            : 'bg-transparent border border-transparent hover:border-slate-300 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue'
                        }`}
                        placeholder="0"
                      />
                    </td>
                    <td className="px-2 py-2 align-top pt-3 text-center">
                      <label className="flex items-center justify-center cursor-pointer group" title="Stock Item (Excluded from PDF)">
                        <input
                          type="checkbox"
                          checked={row.isStock || row._fromStock}
                          onChange={(e) => handleRowChange(row.id, 'isStock', e.target.checked)}
                          disabled={row._fromStock}
                          className={`w-4 h-4 rounded border-slate-300 text-brand-blue focus:ring-brand-blue ${row._fromStock ? 'cursor-not-allowed opacity-60' : 'cursor-pointer group-hover:border-brand-blue'}`}
                        />
                      </label>
                    </td>
                    <td className="px-2 py-2 text-center align-top pt-3">
                      <button
                        onClick={() => removeRow(row.id)}
                        className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="Remove item"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-slate-50 border-t border-slate-200">
            <tr>
              <td colSpan="4" className="px-2 py-2 text-right text-xs font-semibold text-slate-500">SUBTOTAL</td>
              <td className="px-2 py-2 text-right text-sm font-semibold text-slate-700">
                ₹{subTotal.toLocaleString('en-IN')}
              </td>
              <td></td>
            </tr>
            <tr>
              <td colSpan="4" className="px-2 py-2 text-right text-xs font-semibold text-slate-500">
                <div className="flex items-center justify-end gap-2">
                  <span>DISCOUNT</span>
                  <div className="flex items-center bg-white border border-slate-200 rounded text-[11px] overflow-hidden">
                    <button
                      className={`px-2 py-1 ${discountType === 'percentage' ? 'bg-slate-100 font-bold text-brand-blue' : 'text-slate-500 hover:bg-slate-50'}`}
                      onClick={() => setDiscountType('percentage')}
                    >%</button>
                    <button
                      className={`px-2 py-1 border-l border-slate-200 ${discountType === 'fixed' ? 'bg-slate-100 font-bold text-brand-blue' : 'text-slate-500 hover:bg-slate-50'}`}
                      onClick={() => setDiscountType('fixed')}
                    >₹</button>
                  </div>
                  <input
                    type="number"
                    min="0"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    className="w-16 px-1 py-1 text-[12px] bg-white border border-slate-200 rounded outline-none text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    placeholder="0"
                  />
                </div>
              </td>
              <td className="px-2 py-2 text-right text-sm font-semibold text-red-500">
                - ₹{discountAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              </td>
              <td></td>
            </tr>
            <tr>
              <td colSpan="4" className="px-2 py-2 text-right text-xs font-semibold text-slate-500">
                <div className="flex items-center justify-end gap-2">
                  <span>GST</span>
                  <div className="flex items-center bg-white border border-slate-200 rounded text-[11px] overflow-hidden">
                    <button
                      className={`px-2 py-1 ${gstType === 'percentage' ? 'bg-slate-100 font-bold text-brand-blue' : 'text-slate-500 hover:bg-slate-50'}`}
                      onClick={() => setGstType('percentage')}
                    >%</button>
                    <button
                      className={`px-2 py-1 border-l border-slate-200 ${gstType === 'fixed' ? 'bg-slate-100 font-bold text-brand-blue' : 'text-slate-500 hover:bg-slate-50'}`}
                      onClick={() => setGstType('fixed')}
                    >₹</button>
                  </div>
                  <input
                    type="number"
                    min="0"
                    value={gstValue}
                    onChange={(e) => setGstValue(e.target.value)}
                    className="w-16 px-1 py-1 text-[12px] bg-white border border-slate-200 rounded outline-none text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    placeholder="0"
                  />
                </div>
              </td>
              <td className="px-2 py-2 text-right text-sm font-semibold text-slate-700">
                + ₹{gstAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              </td>
              <td></td>
            </tr>
            <tr className="border-t border-slate-200 bg-slate-100/50">
              <td colSpan="4" className="px-2 py-2 text-right text-xs font-bold text-slate-800">TOTAL</td>
              <td className="px-2 py-2 text-right text-sm font-bold text-brand-blue">
                ₹{grandTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};
